import {
  Button,
  Checkbox,
  Description,
  Divider,
  Error,
  Form,
  Header,
  Input,
  Modal,
  Tab,
  Text,
  ValidationStates
} from '@opengov/component-library/dist';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { EReportColumnTypes, IReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import { IColumnAlias, IHiddenControlColumns, ReportControlType } from '@opengov/reporting-types';
import cx from 'classnames2';
import React from 'react';

import { IReportLocale } from '../../../../common/locale';
import { getCalculationString, getCondBreakdown } from '../utilities';

import styles from './ColumnSettingsModal.scss';
import {
  ColumnSettingsModalPromptDeleteable,
  ColumnSettingsModalPromptNonDeleteable
} from './ColumnSettingsModalPrompt';

export interface IDeletionBlockingReportControls {
  isShownBy: boolean;
  isOrganizedBy: boolean;
  isCategorizedBy: boolean;
  isFiltered: boolean;
  dependentColumns: IReportColumn[];
}

export interface IColumnSettingsModal {
  aliases: IColumnAlias[];
  activeTab?: number;
  allHidden: IHiddenControlColumns[];
  column: IReportColumn;
  deletionBlockingReportControls: IDeletionBlockingReportControls;
  oversizeCount: number;
  reportLocaleText: IReportLocale;
  originalColumnName: string;
  onClose(): void;
  onDelete(column): void;
  onSave(columnName: string, displayName: string, types: ReportControlType[]): void;
}

interface IColumnSettingsState {
  catDisabled: boolean;
  catToggle: boolean;
  showDeletePrompt: boolean;
  name: string;
  nameValidationState: ValidationStates;
  orgDisabled: boolean;
  orgToggle: boolean;
  showDisabled: boolean;
  showToggle: boolean;
}

export class ColumnSettingsModal extends React.Component<IColumnSettingsModal, IColumnSettingsState> {
  constructor(props) {
    super(props);

    const { column, allHidden, aliases, oversizeCount } = props;
    const showDefault: boolean =
      column.type === BasicColumnTypes.Currency ||
      column.type === BasicColumnTypes.Numeric ||
      column.type === BasicColumnTypes.Percent;
    const organizeDefault: boolean = column.type === BasicColumnTypes.Text;
    const categorizeDefault: boolean = column.type === BasicColumnTypes.Text;
    const alias = aliases
      ? aliases.filter((a) => {
          return a.originalName === column.name;
        })
      : [];
    const disabled = allHidden.filter((control) => control.columnName === column.name);
    const name = alias.size > 0 ? alias.first().displayName : column.displayName;

    this.state = {
      showDeletePrompt: false,
      showToggle:
        disabled.find((c) => c.type === ReportControlType.Show && c.columnName === column.name) === undefined
          ? showDefault
          : !disabled.find((c) => c.type === ReportControlType.Show && c.columnName === column.name),
      orgToggle:
        disabled.find((c) => c.type === ReportControlType.By && c.columnName === column.name) === undefined
          ? organizeDefault &&
            !(
              column.type === BasicColumnTypes.Text &&
              column.metadata.facts.distinctCount &&
              Number(column.metadata.facts.distinctCount) >= oversizeCount
            )
          : !disabled.find((c) => c.type === ReportControlType.By && c.columnName === column.name),
      catToggle:
        disabled.find((c) => c.type === ReportControlType.Categorize && c.columnName === column.name) === undefined
          ? categorizeDefault &&
            !(
              column.type === BasicColumnTypes.Text &&
              column.metadata.facts.distinctCount &&
              Number(column.metadata.facts.distinctCount) >= oversizeCount
            )
          : !disabled.find((c) => c.type === ReportControlType.Categorize && c.columnName === column.name),
      showDisabled: !showDefault,
      orgDisabled: !organizeDefault,
      catDisabled: !categorizeDefault,
      name: name,
      nameValidationState: ValidationStates.None
    };
  }

  getDisableList() {
    const disabled = [];
    if (!this.state.showToggle) {
      disabled.push(ReportControlType.Show);
    }
    if (!this.state.orgToggle) {
      disabled.push(ReportControlType.By);
    }
    if (!this.state.catToggle) {
      disabled.push(ReportControlType.Categorize);
    }
    return disabled;
  }

  onNameChange = (e: string) => {
    this.setState({
      ...this.state,
      name: e
    });
    if (e.trim().length > 30 || e.trim().length <= 0) {
      this.setState({ nameValidationState: ValidationStates.Error });
    } else {
      this.setState({ nameValidationState: ValidationStates.None });
    }
  };

  // broken out like this to manage cyclomatic complexity
  renderSettings() {
    const { reportLocaleText, column, oversizeCount, originalColumnName } = this.props;
    const showTooLarge =
      column.type === BasicColumnTypes.Text &&
      column.metadata.facts.distinctCount &&
      Number(column.metadata.facts.distinctCount) >= oversizeCount;
    const displayName = this.state.name ? this.state.name : '';

    return (
      <div>
        <div className={styles.displayHeader}>
          <Header variant="h4" data-test="column-settings-naming-title">
            {reportLocaleText.reportTable.columnEdit.naming}
          </Header>
        </div>
        <div className={styles.nameArea}>
          <div className={styles.nameInput}>
            <Form.Item label={reportLocaleText.reportTable.columnEdit.displayName}>
              <Form.CharacterCounter currentCount={this.state.name.length} maxCount={30} dataTest="name-count" />
              <Input.Text
                dataTest="name-input"
                value={displayName}
                onChange={this.onNameChange}
                validationState={this.state.nameValidationState}
              />
              {this.state.nameValidationState === ValidationStates.Error && this.state.name.length <= 30 && (
                <div className={styles.error}>
                  <Error dataTest="reportTitleInputError">{reportLocaleText.reportTable.columnEdit.namingError}</Error>
                </div>
              )}
            </Form.Item>
          </div>
          <div className={styles.vertDivide}>
            <Divider.Vertical />
          </div>
          <div className={styles.nameDefault}>
            <Form.Item label={reportLocaleText.reportTable.columnEdit.defaultName}>
              <div className={styles.nameDefaultText}>
                <Description>{originalColumnName}</Description>
              </div>
            </Form.Item>
          </div>
        </div>
        <div className={styles.horizDivide}>
          <Divider.Horizontal />
        </div>
        <div className={styles.displayHeader}>
          <Header variant="h4" data-test="column-settings-display-title">
            {reportLocaleText.reportTable.columnEdit.display}
          </Header>
        </div>
        <Form.Item label={reportLocaleText.reportTable.columnEdit.availableMenus}>
          <Checkbox.Small
            dataTest="column-settings-show-toggle"
            disabled={this.state.showDisabled}
            checked={this.state.showToggle}
            onChange={() => {
              this.setState({
                showToggle: !this.state.showToggle
              });
            }}
            label={reportLocaleText.reportTable.columnEdit.showMenu}
          />
          <br />
          <Checkbox.Small
            dataTest="column-settings-organize-toggle"
            disabled={this.state.orgDisabled}
            checked={this.state.orgToggle}
            onChange={() => {
              this.setState({
                orgToggle: !this.state.orgToggle
              });
            }}
            label={reportLocaleText.reportTable.columnEdit.organize}
          />
          {showTooLarge && (
            <div className={styles.sizeAlert}>
              <Text size="minimum" color="light" dataTest={'organize-size-alert'}>
                {reportLocaleText.reportTable.columnEdit.tooManyUniqueValues}
              </Text>
            </div>
          )}
          {!showTooLarge && <br />}
          <Checkbox.Small
            dataTest="column-settings-categorize-toggle"
            disabled={this.state.catDisabled}
            checked={this.state.catToggle}
            onChange={() => {
              this.setState({
                catToggle: !this.state.catToggle
              });
            }}
            label={reportLocaleText.reportTable.columnEdit.categorize}
          />
          {showTooLarge && (
            <div className={styles.sizeAlert}>
              <Text size="minimum" color="light" dataTest={'categorize-size-alert'}>
                {reportLocaleText.reportTable.columnEdit.tooManyUniqueValues}
              </Text>
            </div>
          )}
        </Form.Item>
      </div>
    );
  }

  render() {
    const { activeTab, deletionBlockingReportControls, reportLocaleText, onClose, onDelete, onSave, column } =
      this.props;

    const formValid = this.state.nameValidationState !== ValidationStates.Error;
    const displayName = this.state.name ? this.state.name : '';

    return (
      <Modal.Work
        prompt={
          this.state.showDeletePrompt && (
            <div className={`${styles.confirmDelete}`}>
              {deletionBlockingReportControls.isShownBy ||
              deletionBlockingReportControls.isOrganizedBy ||
              deletionBlockingReportControls.isCategorizedBy ||
              deletionBlockingReportControls.isFiltered ||
              deletionBlockingReportControls.dependentColumns.length ? (
                <ColumnSettingsModalPromptNonDeleteable
                  column={column}
                  onCancel={() => {
                    this.setState({ showDeletePrompt: false });
                  }}
                  deletionBlockingReportControls={deletionBlockingReportControls}
                  reportLocaleText={reportLocaleText}
                />
              ) : (
                <ColumnSettingsModalPromptDeleteable
                  column={column}
                  onCancel={() => {
                    this.setState({ showDeletePrompt: false });
                  }}
                  reportLocaleText={reportLocaleText}
                  onDelete={onDelete}
                />
              )}
            </div>
          )
        }
        title={reportLocaleText.reportTable.columnEdit.title}
        onClose={onClose}
      >
        <div className={cx(styles.modalContent)}>
          {(column.columnType === EReportColumnTypes.infix || column.columnType === EReportColumnTypes.conditional) && (
            <Tab.Group activeTabIndex={activeTab || 0} dataTest="custom-column-tabs">
              <Tab.Option label="General">
                <div className={styles.tabbedDialogContainer}>{this.renderSettings()}</div>
              </Tab.Option>
              {column.columnType === EReportColumnTypes.infix && (
                <Tab.Option label="Calcuation" dataTest="calculation-tab">
                  <div className={styles.tabbedDialogContainer}>
                    <Header variant="h4" data-test="column-settings-calculation-title">
                      {reportLocaleText.reportTable.columnEdit.calcHeader}
                    </Header>
                    <div className={styles.nameDefaultText}>
                      <Description dataTest="calculation-description">{getCalculationString(column)}</Description>
                    </div>
                  </div>
                </Tab.Option>
              )}
              {column.columnType === EReportColumnTypes.conditional && (
                <Tab.Option label="Conditional Statement" dataTest="conditional-tab">
                  <div className={styles.tabbedDialogContainer}>
                    <Header variant="h4" data-test="column-settings-conditional-title">
                      {reportLocaleText.reportTable.columnEdit.condHeader}
                    </Header>
                    <div className={styles.nameDefaultText}>
                      <Description dataTest={'conditional-description'}>
                        {getCondBreakdown(column, reportLocaleText)}
                      </Description>
                    </div>
                  </div>
                </Tab.Option>
              )}
            </Tab.Group>
          )}
          {column.columnType !== EReportColumnTypes.infix && column.columnType !== EReportColumnTypes.conditional && (
            <div className={styles.dialogContainer}>{this.renderSettings()}</div>
          )}
          <div role="button" className={styles.controlsWrapper}>
            {[EReportColumnTypes.infix, EReportColumnTypes.conditional].includes(column.columnType) ? (
              <Button
                variant="destructive"
                onClick={() => {
                  this.setState({ showDeletePrompt: true });
                }}
                dataTest="column-settings-delete-button"
              >
                {reportLocaleText.calculations.deleteCalculationModal.deleteButtonText}
              </Button>
            ) : (
              <div />
            )}
            <div>
              <Button onClick={onClose} dataTest="column-settings-close-button">
                {reportLocaleText.cancel}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  onSave(column.name, displayName.trim(), this.getDisableList());
                }}
                disabled={!formValid}
                dataTest="column-settings-save-button"
              >
                {reportLocaleText.save}
              </Button>
            </div>
          </div>
        </div>
      </Modal.Work>
    );
  }
}
